import type { ReactNode } from "react";
import { toast as sonnerToast } from "sonner";

// Lightweight toast adapter that reuses the `sonner` library
// and matches the `{ toast } = useToast()` API used across the app

type ToastVariant = "default" | "destructive" | "success" | "info";

export type ToastOptions = {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  // Allow passing through any extra options without breaking
  // existing or future call sites
  [key: string]: unknown;
};

function toast({ title, description, variant, ...rest }: ToastOptions) {
  const message =
    typeof title === "string" || title === undefined
      ? (title as string | undefined)
      : undefined;
  const desc =
    typeof description === "string" || description === undefined
      ? (description as string | undefined)
      : undefined;

  const options = { description: desc, ...rest } as Parameters<
    typeof sonnerToast
  >[1];

  switch (variant) {
    case "destructive":
      return sonnerToast.error(message ?? "Error", options);
    case "success":
      return sonnerToast.success(message ?? "Success", options);
    case "info":
      return sonnerToast(message ?? "", options);
    default:
      return sonnerToast(message ?? "", options);
  }
}

function useToast() {
  return {
    toast,
    dismiss: sonnerToast.dismiss,
  };
}

export { useToast, toast };
